<template>
  <div :class="getTextPosition">
    <div v-if="hasFields && showText" :class="['text', {[`text--${titleColor}`]: !!titleColor}]">
      <heading
        v-if="isEditMode || title"
        :tagField="fields.headingSize"
        :textField="fields.title"
        :class="['title', textThemeClass, { 'with-hint': isEditMode} ]"
        mobile-size="size-5"
        desktop-size="size-2"
      />
      <sc-text
        v-if="isEditMode || description"
        tag="p"
        :class="[textThemeClass, 'description']"
        :field="fields.description"
      />
    </div>

    <div v-if="hasFields && showControls" :class="['controls', textThemeClass, controlsClass]">
      <div
        v-if="cta1.href"
        ref="cta1"
        class="cta-wrapper cta-wrapper--main"
        @click.capture="openOverlay($event, cta1)"
        @keypress.enter="openOverlay($event, cta1)"
      >
        <CTA
          class='cta'
          :ctaFields="cta1"
          :ctaAppearance="cta1Appearance"
        />
      </div>
      <div
        v-if="cta2.href"
        ref="cta2"
        class="cta-wrapper cta-wrapper--mild"
        @click.capture="openOverlay($event, cta2)"
        @keypress.enter="openOverlay($event, cta2)"
      >
        <CTA
          class='cta'
          :ctaFields="cta2"
          :ctaAppearance="cta2Appearance"
        />
      </div>
    </div>
    <MemberSessionModal
      :name="memberSessionModalName"
      :routeOnConfirm="redirectUrl"
      :routeOnCancel="redirectUrlOnCancel"
    >
        <template v-if="membersOnlyBooking">
          {{ $t('OVERLAY_BLOCK_YOU_MUST_BE_A_MEMBER') }}
        </template>
      </MemberSessionModal>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import { v4 as uuid } from 'uuid'
import { Text } from '@sitecore-jss/sitecore-jss-vue'
import MemberSessionModal, { MEMBER_SESSION_MODAL } from '~/components/member-session-modal'
import {
  ROOM_BOOKING_TYPE,
  ARE_YOU_A_MEMBER,
  MEMBER_SESSION_SHOWN
} from '~/constants/sitecore-keys'
import Heading from '~/components/sitecore/heading'
import CTA from '~/components/sitecore/cta'
import { isExternalLink } from '~/helpers/get-button-type'
import get from '~/helpers/get'
import mapFields from '~/helpers/map-fields'
import isEmptyObject from '~/helpers/is-empty-object'
import UIMixin from '~/mixins/ui'
import location from '~/utils/location'
import getButtonStyle from '~/helpers/get-button-style'
import ThemeMixin from '~/mixins/theme'

const { mapState: mapJssState, mapGetters: mapJssGetters } = createNamespacedHelpers('jss')

const defaultPosition = 'below'

export default {
  name: 'HeroContent',

  components: {
    Heading,
    MemberSessionModal,
    CTA,
    ScText: Text
  },

  mixins: [UIMixin, ThemeMixin],

  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },

  data () {
    return {
      redirectUrl: ''
    }
  },

  computed: {
    ...mapJssState(['route']),
    ...mapJssGetters(['isEditMode']),
    ...mapFields({
      textPosition: 'textPosition.value',
      title: 'title.value',
      titleColor: 'textColour.fields.designSystemToken.value',
      description: 'description.value',
      cta1Appearance: 'cta1Appearance.value',
      cta2Appearance: 'cta2Appearance.value'
    }),

    getTextPosition () {
      return (this.textPosition && this.textPosition.split(' ')[1]) || defaultPosition
    },

    textThemeClass () {
      return this.isDarkTheme ? 'black-theme-text' : 'white-theme-text'
    },

    memberSessionModalName () {
      return `${MEMBER_SESSION_MODAL}-${uuid()}`
    },
    // If the venue is members only and the user presses 'I'm not a member'
    // then redirect to the /bedrooms page without the attached
    // location query in the redirectUrl provided by Sitecore
    redirectUrlOnCancel () {
      return this.membersOnlyBooking
        ? this.localePath({ path: '/bedrooms' })
        : this.redirectUrl
    },

    controlsClass () {
      const style = getButtonStyle(this.cta1Appearance)
      if (!style) return ''

      return `controls--${style}`
    },

    cta1 () {
      return get(this.fields, 'cta1.value', {})
    },

    cta2 () {
      return get(this.fields, 'cta2.value', {})
    },

    hasProfile () {
      return this.profile && !isEmptyObject(this.profile)
    },

    membersOnlyBooking () {
      return get(this.route, 'fields.venue.fields.publicBooking.value', {}) === ROOM_BOOKING_TYPE.closed
    },

    hasFields () {
      return !isEmptyObject(this.fields)
    },

    showText () {
      return this.isEditMode || this.title || this.description
    },

    showControls () {
      return this.isEditMode || this.cta1.href || this.cta2.href
    }
  },
  methods: {
    openOverlay (event, cta) {
      if (this.isOverlayDisabled(cta)) {
        return
      }

      event.preventDefault()
      this.redirectUrl = cta.href
      this.$ui.modal.open(this.memberSessionModalName)
    },

    isOverlayDisabled (cta) {
      return sessionStorage.getItem(MEMBER_SESSION_SHOWN) === 'true'
        || this.hasProfile
        || cta.overlaybehavior !== ARE_YOU_A_MEMBER
    },

    // api for hero component
    contentAction () {
      const { href = '' } = this.cta1

      if (!this.isOverlayDisabled(this.cta1)) {
        this.redirectUrl = href
        this.$ui.modal.open(this.memberSessionModalName)
        return
      }

      if (isExternalLink(href)) {
        location.assign(href)
      } else {
        this.$router.push(href)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .bottom {
    position: relative;
    bottom: auto;
    left: auto;
    padding-left: $space-050;
    padding-right: $space-060;

    @include tablet() {
      position: absolute;
      bottom: $space-110;
      padding-left: $space-060;
    }
  }

  .below {
    padding-left: $space-050;

    @include tablet() {
      padding-left: $space-060;
    }

    .description {
      padding-right: $space-050;

      @include tablet {
        padding-right: 0;
        width: 60%;
      }
    }
  }

  .text {
    padding-top: $space-040;

    &--light {
      color: $color-cream;
    }

    &--dark {
      color: $color-black;
    }

    @include tablet() {
      padding-top: $space-050;
    }
  }

  .description {
    color: inherit;
    margin-top: $space-020;
    @include font('050');

    @include tablet() {
      padding-left: $space-010;
      margin-top: $space-040;
      @include font('060')
    }
  }

  .black-theme-text {
    color: $color-cream;

    @include tablet() {
      color: inherit;
    }
  }

  .white-theme-text {
    color: $color-black;

    @include tablet() {
      color: inherit;
    }
  }

  .controls {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-top: $space-050;

    @include tablet() {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-top: $space-060;
    }

    &--naked {
      @include tablet() {
        margin-top: $space-050;
      }
    }
  }

  .cta-wrapper {
    line-height: 1;

    &:not(:first-child) {
      margin-top: $space-030;

      @include tablet() {
        margin-top: 0;
        margin-left: $space-040
      }
    }


  }
</style>
